import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';

initComponent('.js-filters-drawer', (drawer) => {
  const bodyDrawers = Array.from(
    document.querySelectorAll<HTMLElement>('body > .js-filters-drawer')
  ).filter((el) => el !== drawer);

  if (bodyDrawers.some((el) => el.dataset.open === 'true')) {
    drawer.remove();
    return;
  }

  bodyDrawers.forEach((el) => el.remove());
  if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

  const overlay = drawer.querySelector<HTMLElement>('.js-filters-drawer-overlay');
  const panel = drawer.querySelector<HTMLElement>('.js-filters-drawer-panel');
  const closeTriggers = drawer.querySelectorAll<HTMLElement>('.js-filters-drawer-close');
  const drawerForm = drawer.querySelector<HTMLFormElement>('form');
  const applyButton = drawer.querySelector<HTMLButtonElement>('.js-filters-drawer-apply');
  const filterPanels = drawer.querySelectorAll<HTMLElement>('.ls-filters-drawer__panel[data-filter]');

  if (!overlay || !panel) return;

  const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

  // Hide the site header while the drawer is open so its hamburger menu does not clash with the
  // drawer's close (X). Matches Figma: the header is hidden behind the drawer (its own stacking
  // context means the overlay does not reliably cover it). Queried per call in case the header
  // section was re-rendered by a facets morph. The header's `transition-all` fades it smoothly.
  const setHeaderHidden = (hidden: boolean) => {
    const header = document.querySelector<HTMLElement>('.js-header');
    header?.classList.toggle('opacity-0', hidden);
    header?.classList.toggle('pointer-events-none', hidden);
  };

  const open = () => {
    if (!isMobile()) return;

    drawer.dataset.open = 'true';
    overlay.classList.remove('hidden', 'pointer-events-none');
    closeTriggers.forEach((trigger) => trigger.classList.remove('hidden'));
    setHeaderHidden(true);

    requestAnimationFrame(() => {
      overlay.classList.remove('opacity-0');
      panel.classList.remove('translate-x-full');
      document.body.classList.add('overflow-hidden');
    });
  };

  const close = () => {
    delete drawer.dataset.open;
    overlay.classList.add('opacity-0');
    panel.classList.add('translate-x-full');
    document.body.classList.remove('overflow-hidden');
    setHeaderHidden(false);

    setTimeout(() => {
      overlay.classList.add('hidden', 'pointer-events-none');
      closeTriggers.forEach((trigger) => trigger.classList.add('hidden'));
    }, 400);
  };

  const syncSelection = () => {
    let totalSelected = 0;

    filterPanels.forEach((panel) => {
      const selected = panel.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]:checked'
      ).length;
      totalSelected += selected;

      panel.toggleAttribute('data-active', selected > 0);

      const count = panel.querySelector<HTMLElement>('.js-filters-drawer-count');
      if (count) count.textContent = selected > 0 ? `[ ${selected} ]` : '';
    });

    // The resting sort radio is checked by default (and may be absent when the collection default is
    // not a curated option), so it only counts as a selection when it differs from data-default-sort.
    const defaultSort = drawer.dataset.defaultSort ?? '';
    const checkedSort = drawer.querySelector<HTMLInputElement>('input[name="sort_by"]:checked');
    const sortSelected = checkedSort != null && checkedSort.value !== defaultSort;
    const hasSelection = totalSelected > 0 || sortSelected;

    if (applyButton) {
      applyButton.classList.toggle('pointer-events-none', !hasSelection);
      applyButton.classList.toggle('bg-surface-white', hasSelection);
      applyButton.classList.toggle('text-text-invert', hasSelection);
      applyButton.classList.toggle('bg-surface-white-15', !hasSelection);
      applyButton.classList.toggle('text-text-primary', !hasSelection);
    }
  };

  const sectionId = drawer.dataset.sectionId;
  const getFacetsForm = (): any =>
    document.querySelector(
      sectionId
        ? `facets-form-component[section-id="${sectionId}"]`
        : 'facets-form-component'
    );

  const applyByURL = (url: string) => {
    const facetsForm = getFacetsForm();
    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url);
    } else {
      window.location.assign(url);
    }
  };

  // Live availability: the drawer is portaled to <body>, outside the section morph, so it refreshes
  // itself. Only `disabled` can change between renders, so we sync just that — no innerHTML swap.
  let availabilityController: AbortController | null = null;
  let availabilityTimer: number | undefined;

  // Values disabled in the unfiltered state, so "Clear all" restores availability with no fetch.
  const unfilteredDisabledIds = new Set<string>();
  let snapshotIsUnfiltered = false;

  const hasActiveFilterParams = (params: URLSearchParams) => {
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return true;
    }
    return false;
  };

  // Set `disabled` on every (non-price) value. Buttons style it via `has-[:disabled]`; swatches
  // carry it on the wrapping <li>, so toggle that too.
  const applyDisabledState = (shouldDisable: (input: HTMLInputElement) => boolean) => {
    filterPanels.forEach((filterPanel) => {
      const values = filterPanel.querySelector<HTMLElement>(':scope > .mt-12');
      if (!values || values.querySelector('input[type="number"]')) return;

      values.querySelectorAll<HTMLInputElement>('input[id]').forEach((input) => {
        const disabled = shouldDisable(input);
        input.disabled = disabled;
        const li = input.closest('li');
        if (li) {
          li.classList.toggle('opacity-40', disabled);
          li.classList.toggle('pointer-events-none', disabled);
        }
      });
    });
  };

  const captureUnfilteredDisabled = () => {
    unfilteredDisabledIds.clear();
    filterPanels.forEach((filterPanel) => {
      const values = filterPanel.querySelector<HTMLElement>(':scope > .mt-12');
      if (!values || values.querySelector('input[type="number"]')) return;
      values.querySelectorAll<HTMLInputElement>('input[id]:disabled').forEach((input) => {
        unfilteredDisabledIds.add(input.id);
      });
    });
    snapshotIsUnfiltered = true;
  };

  const syncAvailability = async () => {
    if (!drawerForm || !sectionId) return;

    const facetsForm = getFacetsForm();
    let params: URLSearchParams;
    if (facetsForm && typeof facetsForm.createURLParameters === 'function') {
      params = facetsForm.createURLParameters(new FormData(drawerForm));
    } else {
      params = new URLSearchParams();
      new FormData(drawerForm).forEach((value, key) => {
        if (typeof value === 'string' && value !== '') params.append(key, value);
      });
    }
    const isUnfiltered = !hasActiveFilterParams(params);
    params.set('section_id', sectionId);

    availabilityController?.abort();
    availabilityController = new AbortController();

    try {
      const response = await fetch(`${window.location.pathname}?${params.toString()}`, {
        signal: availabilityController.signal,
      });
      if (!response.ok) return;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const freshDrawer = doc.querySelector('.js-filters-drawer');
      if (!freshDrawer) return;

      const freshDisabledIds = new Set<string>();
      freshDrawer
        .querySelectorAll<HTMLInputElement>('.ls-filters-drawer__panel[data-filter] input[id]:disabled')
        .forEach((input) => freshDisabledIds.add(input.id));

      applyDisabledState((input) => freshDisabledIds.has(input.id));

      // A fetch with no filter params reflects the unfiltered state, so refresh the snapshot.
      if (isUnfiltered) captureUnfilteredDisabled();
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') console.error(error);
    }
  };

  const scheduleAvailabilitySync = () => {
    window.clearTimeout(availabilityTimer);
    availabilityTimer = window.setTimeout(syncAvailability, 250);
  };

  // Seed the snapshot from the initial render when the page loaded with no filters applied.
  if (!hasActiveFilterParams(new URL(window.location.href).searchParams)) {
    captureUnfilteredDisabled();
  }

  const applyFilters = () => {
    const facetsForm = getFacetsForm();
    const url = new URL(window.location.pathname, window.location.origin);

    if (drawerForm && facetsForm && typeof facetsForm.createURLParameters === 'function') {
      const params: URLSearchParams = facetsForm.createURLParameters(new FormData(drawerForm));
      params.forEach((value, key) => url.searchParams.append(key, value));
    } else if (drawerForm) {
      new FormData(drawerForm).forEach((value, key) => {
        if (typeof value === 'string' && value !== '') url.searchParams.append(key, value);
      });
    }

    applyByURL(url.toString());
    close();
  };

  const clearAll = () => {
    drawer
      .querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]')
      .forEach((input) => {
        input.checked = false;
      });
    // Reset price (free-text) inputs too — a full drawer reset.
    drawer.querySelectorAll<HTMLInputElement>('input[type="number"]').forEach((input) => {
      input.value = '';
    });
    // Re-check the default sort (Best sellers): clear resets sort to its resting value, not to none.
    const defaultSort = drawer.dataset.defaultSort ?? '';
    const defaultSortRadio = drawer.querySelector<HTMLInputElement>(
      `input[name="sort_by"][value="${defaultSort}"]`
    );
    if (defaultSortRadio) defaultSortRadio.checked = true;
    syncSelection();

    const url = new URL(window.location.pathname, window.location.origin);
    const query = new URL(window.location.href).searchParams.get('q');
    if (query) url.searchParams.set('q', query);

    applyByURL(url.toString());

    // Restore availability: instant from the snapshot if known, else fetch it (which seeds it).
    if (snapshotIsUnfiltered) {
      applyDisabledState((input) => unfilteredDisabledIds.has(input.id));
    } else {
      syncAvailability();
    }
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    applyFilters();
  };

  const handleChange = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target instanceof HTMLInputElement && (target.type === 'checkbox' || target.type === 'radio')) {
      syncSelection();
      scheduleAvailabilitySync();
    }
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  const handleResize = () => {
    if (!isMobile()) close();
  };

  bindAll(document, '.js-filters-drawer-open', 'click', open);
  bindAll(drawer, '.js-filters-drawer-close', 'click', close);
  bindAll(drawer, '.js-filters-drawer-clear', 'click', clearAll);
  overlay.addEventListener('click', close);
  drawer.addEventListener('change', handleChange);
  drawerForm?.addEventListener('submit', handleSubmit);
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', handleResize);
  window.addEventListener('filters-drawer:open', open);

  syncSelection();

  return {
    unload() {
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('filters-drawer:open', open);
      window.clearTimeout(availabilityTimer);
      availabilityController?.abort();
      setHeaderHidden(false);
      drawer.remove();
    },
  };
});
